import { Ban, RotateCcw, Trash2 } from "lucide-react";
import {
  Form,
  useCreate,
  useDelete,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useMemo } from "react";
import type { FieldValues } from "react-hook-form";

import { SaveButton } from "@/components/admin/form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  timestampToDateOnly,
  toDateOnly,
  toMonth,
} from "../../providers/commons/financeSchedule";
import type { FinanceCharge } from "../../types";
import { toChargeData } from "../financeFormModels";
import { useFinanceErrorHandler } from "../useFinanceErrorHandler";
import { ChargeInputs } from "./ChargeInputs";

const RESOURCE = "finance_charges";

export interface ChargeDialogProps {
  open: boolean;
  onClose: () => void;
  /** Charge to edit; when absent the dialog creates a one-off charge */
  charge?: FinanceCharge | null;
}

const toFormRecord = (charge: FinanceCharge | null | undefined) => {
  if (!charge) {
    const today = new Date();
    return {
      kind: "one_off",
      competence_month: toMonth(today),
      due_date: toDateOnly(today),
      invoice_files: [],
      receipt_files: [],
    };
  }
  return {
    ...charge,
    sent_on: timestampToDateOnly(charge.sent_at),
    paid_on: timestampToDateOnly(charge.paid_at),
  };
};

export const ChargeDialog = ({ open, onClose, charge }: ChargeDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const onError = useFinanceErrorHandler();
  const [create] = useCreate();
  const [update, { isPending: isUpdating }] = useUpdate();
  const [deleteOne, { isPending: isDeleting }] = useDelete();
  const record = useMemo(() => toFormRecord(charge), [charge]);

  const done = (message: string) => () => {
    notify(message);
    onClose();
  };

  const handleSubmit = async (values: FieldValues) => {
    const data = toChargeData(values, charge);
    if (charge) {
      await update(
        RESOURCE,
        { id: charge.id, data, previousData: charge },
        { onSuccess: done("crm.finance.charges.updated"), onError },
      );
      return;
    }
    await create(
      RESOURCE,
      { data },
      { onSuccess: done("crm.finance.charges.created"), onError },
    );
  };

  const toggleCancelled = () => {
    if (!charge) return;
    update(
      RESOURCE,
      {
        id: charge.id,
        data: {
          cancelled_at: charge.cancelled_at ? null : new Date().toISOString(),
        },
        previousData: charge,
      },
      {
        onSuccess: done(
          charge.cancelled_at
            ? "crm.finance.charges.restored"
            : "crm.finance.charges.cancelled",
        ),
        onError,
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-h-9/10 overflow-y-auto lg:max-w-2xl">
        {open && (
          <Form
            record={record}
            onSubmit={handleSubmit}
            className="flex flex-col gap-4"
          >
            <DialogHeader>
              <DialogTitle>
                {translate(
                  charge
                    ? "crm.finance.charges.edit"
                    : "crm.finance.charges.create",
                )}
              </DialogTitle>
            </DialogHeader>
            <ChargeInputs />
            <DialogFooter className="w-full flex-wrap gap-2 sm:justify-between">
              {charge ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-destructive"
                    disabled={isDeleting}
                    onClick={() =>
                      deleteOne(
                        RESOURCE,
                        { id: charge.id, previousData: charge },
                        {
                          onSuccess: done("crm.finance.charges.deleted"),
                          onError,
                        },
                      )
                    }
                  >
                    <Trash2 />
                    {translate("ra.action.delete")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isUpdating}
                    onClick={toggleCancelled}
                  >
                    {charge.cancelled_at ? <RotateCcw /> : <Ban />}
                    {translate(
                      charge.cancelled_at
                        ? "crm.finance.charges.restore"
                        : "crm.finance.charges.cancel",
                    )}
                  </Button>
                </div>
              ) : (
                <span />
              )}
              <SaveButton />
            </DialogFooter>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
